import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { Department, TaskScopeNameEnum } from 'graphql-common';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import useModalManagement from '@lib/hooks/useModalManagement';
import formStyles from '@lib/assets/styles/Form.module.scss';
import DepartmentFieldNames from '@lib/enums/fieldNames/departmentFieldNames';
import TasksModalTableMultiselect from 'components/TasksModalTableMultiselect/TasksModalTableMultiselect';
import InterventionsModalTableMultiselect from 'components/InterventionsModalTableMultiselect/InterventionsModalTableMultiselect';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import useSearchListQueries from '../utils/useSearchListQueries';
import useDepartmentActions from '../utils/useDepartmentActions';
import getDepartmentFormFields from '../utils/getDepartmentFormFields';
import { UseDepartmentQueriesResult } from '../utils/useDepartmentQueries';
import { DepartmentViewMode } from '../enums';
import DepartmentParentSelectModal from '../components/DepartmentParentSelectModal/DepartmentParentSelectModal';

const SELECT_TASKS_MODAL = 'select-department-tasks';
const SELECT_INTERVENTIONS_MODAL = 'select-department-interventions';
const SELECT_PARENT_DEPARTMENT_MODAL = 'select-parent-department';

// BE validation error paths use the DepartmentCreate/UpdateInputObject keys
// (managerId, parentId, teamLinks, taskLinks, interventionLinks), which don't
// match this form's field names — remap so errors attach to the right field.
const GRAPHQL_ERRORS_REPLACE_PATH_MAP = {
  managerId: DepartmentFieldNames.Manager,
  parentId: DepartmentFieldNames.ParentDepartment,
  teamLinks: DepartmentFieldNames.AccessGroups,
  taskLinks: DepartmentFieldNames.Tasks,
  interventionLinks: DepartmentFieldNames.Interventions,
};

type Props = {
  viewMode: DepartmentViewMode;
  useDepartmentQueriesResult: UseDepartmentQueriesResult;
};

export default function DepartmentFormPage({
  viewMode,
  useDepartmentQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const navigate = useNavigate();
  const useSearchListQueriesResult = useSearchListQueries();
  const useModalManagementResult = useModalManagement([
    SELECT_TASKS_MODAL,
    SELECT_INTERVENTIONS_MODAL,
    SELECT_PARENT_DEPARTMENT_MODAL,
  ]);
  const { defaultValues, id, isCreatePage, loading, mutationError, onSubmit } =
    useDepartmentActions({
      viewMode,
      useDepartmentQueriesResult,
    });
  const { useUsersQueryHookResult } = useDepartmentQueriesResult;
  const { allIds: membersIds = [] } = useUsersQueryHookResult;

  const openTasksModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const onCloseTasksModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const onTasksSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        DepartmentFieldNames.Tasks,
        values.length ? values.map((groupKey) => ({ value: groupKey })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const openInterventionsModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_INTERVENTIONS_MODAL);
  }, [useModalManagementResult]);

  const onCloseInterventionsModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_INTERVENTIONS_MODAL);
  }, [useModalManagementResult]);

  const onInterventionsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        DepartmentFieldNames.Interventions,
        values.length
          ? values.map((interventionId) => ({ value: interventionId }))
          : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const openDepartmentModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_PARENT_DEPARTMENT_MODAL);
  }, [useModalManagementResult]);

  const onCloseDepartmentModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_PARENT_DEPARTMENT_MODAL);
  }, [useModalManagementResult]);

  const onDepartmentSelect = useCallback((department: Department | null) => {
    if (formRef.current) {
      formRef.current.setValue(
        DepartmentFieldNames.ParentDepartment,
        department
          ? {
              value: department.id,
              label: department.name,
              data: { path: department.path },
            }
          : null,
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const fields = getDepartmentFormFields({
    t,
    departmentId: id,
    getDepartmentsLoadOptions:
      useSearchListQueriesResult.getDepartmentsLoadOptions,
    getUsersLoadOptions: useSearchListQueriesResult.getUsersLoadOptions,
    getTeamsLoadOptions: useSearchListQueriesResult.getTeamsLoadOptions,
    getInterventionsLoadOptions:
      useSearchListQueriesResult.getInterventionsLoadOptions,
    getTasksLoadOptions: useSearchListQueriesResult.getTasksLoadOptions,
    membersIds,
    openTasksModal,
    openInterventionsModal,
    openDepartmentModal,
  });

  const actionsComponent = () => (
    <ActionsRow
      className={formStyles.formActions}
      actions={[
        <Button
          onClick={() => navigate(-1)}
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          fullWidth
        />,
        <Button
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t(isCreatePage ? 'create' : 'save-changes')}
          leftIcon="check"
          type="submit"
          fullWidth
        />,
      ]}
    />
  );

  return (
    <DashboardContentMaxWidth>
      <PageHeader
        title={t(isCreatePage ? 'create-department' : 'edit-department')}
      />
      <PageBody>
        <WhiteBox verticalPaddings="large">
          <Form
            actionsComponent={actionsComponent}
            actionsComponentProps={{ isCreatePage }}
            defaultValues={defaultValues}
            fields={fields}
            formId="department"
            formRef={formRef}
            graphQLErrors={getGraphQLErrors(mutationError)}
            graphQLErrorsReplacePathMap={GRAPHQL_ERRORS_REPLACE_PATH_MAP}
            loading={loading}
            onSubmit={onSubmit}
          />
        </WhiteBox>
      </PageBody>
      <TasksModalTableMultiselect
        isOpen={useModalManagementResult.isModalOpened(SELECT_TASKS_MODAL)}
        onClose={onCloseTasksModal}
        onSelect={onTasksSelect}
        formRef={formRef}
        fieldName={DepartmentFieldNames.Tasks}
        scopeName={TaskScopeNameEnum.Active}
      />
      <InterventionsModalTableMultiselect
        isOpen={useModalManagementResult.isModalOpened(
          SELECT_INTERVENTIONS_MODAL,
        )}
        onClose={onCloseInterventionsModal}
        onSelect={onInterventionsSelect}
        formRef={formRef}
        fieldName={DepartmentFieldNames.Interventions}
        departmentId={id}
      />
      <DepartmentParentSelectModal
        excludeId={id}
        isOpen={useModalManagementResult.isModalOpened(
          SELECT_PARENT_DEPARTMENT_MODAL,
        )}
        onClose={onCloseDepartmentModal}
        onSelect={onDepartmentSelect}
        selected={_get(formRef.current?.values, [
          DepartmentFieldNames.ParentDepartment,
          'value',
        ])}
      />
    </DashboardContentMaxWidth>
  );
}
